import { useEffect, useMemo, useState } from "react";
import { ulid } from "ulid";
import type { CookSessionApiItem } from "@recipevault/shared";

export type ActiveTimer = CookSessionApiItem["activeTimers"][number];

export interface UseTimers {
  running: ActiveTimer[];
  fired: ActiveTimer[];
  shortestRemainingMs: number | null;
  startTimer: (label: string, stepId: string, minutes: number) => ActiveTimer[];
  extendTimer: (id: string, addMs: number) => ActiveTimer[];
  stopTimer: (id: string) => ActiveTimer[];
}

function iso(ms: number): string {
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, "Z");
}

/**
 * Running/fired timer engine over `session.activeTimers`. Ticks `now` every second
 * so countdowns and the running→fired transition update live. The mutators are PURE
 * array transforms returning the next `activeTimers` array — the caller persists it
 * via sync.patchCookSessionOptimistic(session.id, { activeTimers: next }).
 */
export function useTimers(session: Pick<CookSessionApiItem, "activeTimers">): UseTimers {
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const timers = session.activeTimers;

  const { running, fired } = useMemo(() => {
    const run: ActiveTimer[] = [];
    const fire: ActiveTimer[] = [];
    for (const t of timers) {
      if (new Date(t.endsAt).getTime() > now) run.push(t);
      else fire.push(t);
    }
    const bySoonest = (a: ActiveTimer, b: ActiveTimer) =>
      new Date(a.endsAt).getTime() - new Date(b.endsAt).getTime();
    run.sort(bySoonest);
    fire.sort(bySoonest);
    return { running: run, fired: fire };
  }, [timers, now]);

  const shortestRemainingMs =
    running.length > 0 ? Math.max(0, new Date(running[0]!.endsAt).getTime() - now) : null;

  function startTimer(label: string, stepId: string, minutes: number): ActiveTimer[] {
    return [...timers, { id: `timer_${ulid()}`, stepId, label, endsAt: iso(Date.now() + minutes * 60_000) }];
  }
  function extendTimer(id: string, addMs: number): ActiveTimer[] {
    return timers.map((t) => (t.id === id ? { ...t, endsAt: iso(new Date(t.endsAt).getTime() + addMs) } : t));
  }
  function stopTimer(id: string): ActiveTimer[] {
    return timers.filter((t) => t.id !== id);
  }

  return { running, fired, shortestRemainingMs, startTimer, extendTimer, stopTimer };
}
